/* oeno-gift-modal.js — Selling plans + Gift properties (direct product variant) */
(() => {
  const MODAL_ID = "oenoGiftModal";
  const DATA_SCRIPT_ID = "oenoGiftProducts";

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];

  const DELIVERY_LABELS = {
    url: "🔗 Par lien",
    print: "🖨️ Imprimer (PDF)"
  };

  // ---------------------------
  // Utils
  // ---------------------------
  const moneyFromCents = (cents) => {
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value);
    } catch {
      return `${value.toFixed(2)} €`;
    }
  };

  const readDataFromDom = () => {
    const el = document.getElementById(DATA_SCRIPT_ID);
    if (!el) {
      console.warn(`[OENO] Missing #${DATA_SCRIPT_ID}`);
      return { products: [] };
    }
    try {
      const data = JSON.parse(el.textContent || "{}");
      return { products: Array.isArray(data.products) ? data.products : [] };
    } catch (e) {
      console.error(`[OENO] Invalid JSON in #${DATA_SCRIPT_ID}`, e);
      return { products: [] };
    }
  };

  // ---------------------------
  // Scroll lock
  // ---------------------------
  const lockScroll = () => {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add("oeno-modal-open");
    document.body.classList.add("oeno-modal-open");
    document.body.dataset.oenoScrollY = String(y);
    document.body.style.top = `-${y}px`;
  };

  const unlockScroll = () => {
    const y = parseInt(document.body.dataset.oenoScrollY || "0", 10) || 0;
    document.documentElement.classList.remove("oeno-modal-open");
    document.body.classList.remove("oeno-modal-open");
    document.body.style.top = "";
    window.scrollTo(0, y);
    delete document.body.dataset.oenoScrollY;
  };

  // ---------------------------
  // Modal open/close
  // ---------------------------
  const openModal = (modal, ctx = {}) => {
    if (!modal) return;

    modal.dataset.productId = ctx.productId || "";
    modal.dataset.productHandle = ctx.handle || "";

    // Reset state
    modal.dataset.selectedHandle = "";
    modal.dataset.selectedDuration = "";
    modal.dataset.selectedVariantId = "";
    modal.dataset.selectedSellingPlanId = "";
    modal.dataset.selectedBoxTitle = "";
    modal.dataset.selectedDurationPrice = "";

    modal.setAttribute("aria-hidden", "false");
    modal.classList.remove("is-open");
    void modal.offsetHeight;
    modal.classList.add("is-open");

    lockScroll();

    // Reset form fields
    const nameEl = qs("#oenoGiftName", modal);
    const donorEl = qs("#oenoGiftDonor", modal);
    const msgEl = qs("#oenoGiftMsg", modal);
    if (nameEl) nameEl.value = "";
    if (donorEl) donorEl.value = "";
    if (msgEl) msgEl.value = "";

    const printRadio = qs('input[name="oenoGiftDelivery"][value="print"]', modal);
    if (printRadio) {
      printRadio.checked = true;
      updateDeliveryUI(modal);
    }

    hydrateModal(modal);
    setStep(modal, 1);
    clearError(modal);

    const focusTarget =
      qs('[data-oeno-step="1"] [data-oeno-autofocus]', modal) ||
      qs('button, [href], input, textarea, select', modal);
    focusTarget?.focus?.();
  };

  const closeModal = (modal) => {
    if (!modal) return;
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    unlockScroll();
  };

  // ---------------------------
  // Delivery UI
  // ---------------------------
  const updateDeliveryUI = (modal) => {
    const checked = qs('input[name="oenoGiftDelivery"]:checked', modal);
    const val = checked && checked.value !== "email" ? checked.value : "print";
    qsa(".oeno-delivery-card__inner", modal).forEach((el) => {
      el.classList.toggle("is-selected", el.dataset.delivery === val);
    });
  };

  // ---------------------------
  // Stepper
  // ---------------------------
  const setStep = (modal, step) => {
    const steps = qsa("[data-oeno-step]", modal);
    const total = steps.length || 4;

    step = Math.max(1, Math.min(total, step));
    modal.dataset.step = String(step);

    steps.forEach((el) => (el.hidden = el.dataset.oenoStep !== String(step)));

    qsa(".oeno-stepper__item", modal).forEach((item) => {
      const s = parseInt(item.dataset.step || "0", 10);
      item.classList.toggle("is-active", s === step);
      item.classList.toggle("is-done", s < step);
    });

    const btnPrev = qs("[data-oeno-prev]", modal);
    const btnNext = qs("[data-oeno-next]", modal);
    const btnAdd = qs("[data-oeno-add]", modal);

    if (btnPrev) btnPrev.disabled = step === 1;
    if (btnNext) btnNext.hidden = step === total;
    if (btnAdd) btnAdd.hidden = step !== total;

    if (step === total) updateRecap(modal);
  };

  // ---------------------------
  // State
  // ---------------------------
  const getSelected = (modal) => ({
    handle: modal.dataset.selectedHandle || "",
    duration: modal.dataset.selectedDuration || "",
    variantId: modal.dataset.selectedVariantId || "",
    sellingPlanId: modal.dataset.selectedSellingPlanId || "",
    durationPrice: modal.dataset.selectedDurationPrice || "",
    boxTitle: modal.dataset.selectedBoxTitle || "",
    name: (qs("#oenoGiftName", modal)?.value || "").trim(),
    donor: (qs("#oenoGiftDonor", modal)?.value || "").trim(),
    message: (qs("#oenoGiftMsg", modal)?.value || "").trim(),
    delivery: (() => {
      const value = qs('input[name="oenoGiftDelivery"]:checked', modal)?.value || "print";
      return value === "email" ? "print" : value;
    })()
  });

  // ---------------------------
  // Error
  // ---------------------------
  const showError = (modal, msg) => {
    const box = qs("[data-oeno-error]", modal);
    if (!box) return;
    box.textContent = msg;
    box.hidden = false;
    box.scrollIntoView({ block: "nearest" });
  };

  const clearError = (modal) => {
    const box = qs("[data-oeno-error]", modal);
    if (!box) return;
    box.hidden = true;
    box.textContent = "";
  };

  const validateStep = (modal) => {
    const step = parseInt(modal.dataset.step || "1", 10);
    const s = getSelected(modal);

    if (step === 1 && !s.handle) return false;
    if (step === 2 && (!s.variantId || !s.sellingPlanId)) return false;
    return true;
  };

  // ---------------------------
  // Hydration
  // ---------------------------
  let OENO_PRODUCTS = [];

  const hydrateModal = (modal) => {
    if (!OENO_PRODUCTS.length) {
      const { products } = readDataFromDom();
      OENO_PRODUCTS = products || [];
      console.log("[OENO] Products loaded:", OENO_PRODUCTS.length, OENO_PRODUCTS);
    }

    renderBoxes(modal);
    renderDurations(modal);
  };

  // ---------------------------
  // Render step 1: boxes
  // ---------------------------
  const renderBoxes = (modal) => {
    const grid = qs(".oeno-box-grid", modal);
    if (!grid) return;

    grid.innerHTML = OENO_PRODUCTS.map((p) => {
      const img = p.image
        ? `<div class="oeno-box-card__media"><img src="${p.image}" alt="" loading="lazy"></div>`
        : `<div class="oeno-box-card__media oeno-box-card__media--empty"></div>`;

      return `
        <button type="button"
          class="oeno-box-card"
          data-oeno-box
          data-handle="${p.handle}"
          data-title="${encodeURIComponent(p.title || "")}"
          data-variant-id="${p.variant_id || ""}">
          ${img}
          <div class="oeno-box-card__title">${p.title || ""}</div>
          <div class="oeno-box-card__sub">${p.subtitle || ""}</div>
        </button>
      `;
    }).join("");

    const selectedHandle = modal.dataset.selectedHandle || "";
    if (selectedHandle) {
      qsa("[data-oeno-box]", grid).forEach((b) => b.classList.toggle("is-selected", b.dataset.handle === selectedHandle));
    }
  };

  // ---------------------------
  // Render step 2: durations from selling plans
  // ---------------------------
  const renderDurations = (modal) => {
    const row = qs(".oeno-duration-row", modal);
    if (!row) return;

    const handle = modal.dataset.selectedHandle || "";
    const p = OENO_PRODUCTS.find((x) => x.handle === handle);

    if (!p) {
      row.innerHTML = `<div class="oeno-muted">Choisis d'abord une box.</div>`;
      return;
    }

    const plans = p.seal_plans || [];
    console.log("[OENO] Selling plans for", p.title, ":", plans);

    if (!plans.length) {
      row.innerHTML = `<div class="oeno-muted">Aucun abonnement disponible pour cette box.</div>`;
      return;
    }

    // Trier par durée
    const sorted = [...plans].sort((a, b) => Number(a.duration) - Number(b.duration));

    const chips = sorted.map((sp) => {
      const price = moneyFromCents(sp.price);
      const dur = sp.duration || sp.name;
      const monthlyBase = Math.max(...plans.map((plan) => Number(plan.price) / Number(plan.duration)).filter((value) => Number.isFinite(value) && value > 0));
      const regularTotal = monthlyBase * Number(dur);
      const discount = regularTotal > Number(sp.price) ? Math.round((1 - Number(sp.price) / regularTotal) * 100) : 0;

      return `
        <button type="button"
          class="oeno-chip"
          data-oeno-duration
          data-duration="${dur}"
          data-selling-plan-id="${sp.id}"
          data-variant-id="${p.variant_id}"
          data-price="${encodeURIComponent(price)}">
          <span class="oeno-chip__mois">${dur} mois</span>
          <span class="oeno-chip__price">${price} au total</span>
          ${discount > 0 ? `<span class="oeno-chip__discount" style="display:inline-block;padding:3px 8px;border-radius:7px;background:#f1586c;color:#fff;white-space:nowrap;font-weight:700;">−${discount} %</span>` : ""}
        </button>
      `;
    }).join("");

    row.innerHTML = chips;

    const selectedDuration = modal.dataset.selectedDuration || "";
    if (selectedDuration) {
      qsa("[data-oeno-duration]", row).forEach((c) => c.classList.toggle("is-selected", c.dataset.duration === selectedDuration));
    }
  };

  // ---------------------------
  // Recap
  // ---------------------------
  const updateRecap = (modal) => {
    const s = getSelected(modal);

    const recapBox = qs("[data-oeno-recap-box]", modal);
    const recapDur = qs("[data-oeno-recap-duration]", modal);
    const recapPrice = qs("[data-oeno-recap-price]", modal);
    const recapTo = qs("[data-oeno-recap-to]", modal);
    const recapFrom = qs("[data-oeno-recap-from]", modal);
    const recapDelivery = qs("[data-oeno-recap-delivery]", modal);
    const recapMsg = qs("[data-oeno-recap-msg]", modal);
    const recapMsgRow = qs("[data-oeno-recap-msg-row]", modal);

    if (recapBox) recapBox.textContent = s.boxTitle || "—";
    if (recapDur) recapDur.textContent = s.duration ? `${s.duration} mois` : "—";
    if (recapPrice) recapPrice.textContent = s.durationPrice || "—";
    if (recapTo) recapTo.textContent = s.name || "—";
    if (recapFrom) recapFrom.textContent = s.donor || "—";
    if (recapDelivery) recapDelivery.textContent = DELIVERY_LABELS[s.delivery] || s.delivery;

    if (recapMsgRow) {
      if (s.message) {
        recapMsgRow.style.display = "";
        if (recapMsg) recapMsg.textContent = `"${s.message}"`;
      } else {
        recapMsgRow.style.display = "none";
      }
    }

    // Sync hidden form
    const idInput = qs('input[name="id"]', modal);
    if (idInput) idInput.value = s.variantId || "";
    const spInput = qs('input[name="selling_plan"]', modal);
    if (spInput) spInput.value = s.sellingPlanId || "";
  };

  // ---------------------------
  // Add to cart — actual product variant + selling_plan + _gift properties
  // ---------------------------
  const addGiftToCart = async (modal) => {
    const s = getSelected(modal);
    if (!s.variantId || !s.sellingPlanId) {
      showError(modal, "Choisis une durée.");
      return;
    }

    const btnAdd = qs("[data-oeno-add]", modal);
    if (btnAdd) {
      btnAdd.disabled = true;
      btnAdd.textContent = "Ajout en cours…";
    }

    const payload = {
      items: [
        {
          id: Number(s.variantId),
          quantity: 1,
          selling_plan: Number(s.sellingPlanId),
          properties: {
            // Visibles dans le panier
            "Type": "🎁 Cadeau",
            "Pour": s.name || "Bénéficiaire",
            "De la part de": s.donor || "—",
            "Livraison": DELIVERY_LABELS[s.delivery] || s.delivery,
            "Durée": `${s.duration} mois`,
            "Validité": "1 an",
            // Cachées (préfixe _) pour le webhook
            "_gift": "true",
            "_gift_recipient_email": "",
            "_gift_recipient_name": s.name,
            "_gift_donor_name": s.donor,
            "_gift_message": s.message,
            "_gift_delivery": s.delivery,
            "_gift_box_handle": s.handle,
            "_gift_duration": s.duration
          }
        }
      ]
    };

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("[OENO] /cart/add.js error", txt);
        showError(modal, "Erreur lors de l'ajout au panier.");
        if (btnAdd) {
          btnAdd.disabled = false;
          btnAdd.textContent = "🎁 Ajouter le bon cadeau au panier";
        }
        return;
      }

      window.location.href = "/cart";
    } catch (e) {
      console.error("[OENO] addGiftToCart error", e);
      showError(modal, "Erreur réseau.");
      if (btnAdd) {
        btnAdd.disabled = false;
        btnAdd.textContent = "🎁 Ajouter le bon cadeau au panier";
      }
    }
  };

  // ---------------------------
  // Events
  // ---------------------------
  document.addEventListener("click", (e) => {
    const modal = qs(`#${MODAL_ID}`);

    // Open
    const openBtn = e.target.closest(".js-open-oeno-gift");
    if (openBtn) {
      e.preventDefault();
      openModal(modal, {
        productId: openBtn.dataset.productId,
        handle: openBtn.dataset.productHandle
      });
      return;
    }

    if (!modal) return;

    // Close
    if (e.target.closest("[data-oeno-gift-close]") || e.target.matches(".oeno-gift-modal__backdrop")) {
      if (modal.classList.contains("is-open")) closeModal(modal);
      return;
    }

    if (!modal.classList.contains("is-open")) return;

    // Delivery card selection
    const deliveryCard = e.target.closest('.oeno-delivery-card');
    if (deliveryCard) {
      const radio = qs('input[type="radio"]', deliveryCard);
      if (radio) {
        radio.checked = true;
        updateDeliveryUI(modal);
      }
      return;
    }

    // Prev
    const prev = e.target.closest("[data-oeno-prev]");
    if (prev) {
      e.preventDefault();
      clearError(modal);
      setStep(modal, parseInt(modal.dataset.step || "1", 10) - 1);
      return;
    }

    // Next
    const next = e.target.closest("[data-oeno-next]");
    if (next) {
      e.preventDefault();
      clearError(modal);

      if (!validateStep(modal)) {
        const step = parseInt(modal.dataset.step || "1", 10);
        if (step === 1) showError(modal, "Choisis une box.");
        if (step === 2) showError(modal, "Choisis une durée.");
        return;
      }

      setStep(modal, parseInt(modal.dataset.step || "1", 10) + 1);
      return;
    }

    // Add
    const add = e.target.closest("[data-oeno-add]");
    if (add) {
      e.preventDefault();
      clearError(modal);

      const s = getSelected(modal);
      if (!s.variantId || !s.sellingPlanId) {
        showError(modal, "Choisis une box et une durée.");
        setStep(modal, 2);
        return;
      }

      addGiftToCart(modal);
      return;
    }

    // Box select
    const boxBtn = e.target.closest("[data-oeno-box]");
    if (boxBtn) {
      e.preventDefault();

      const handle = boxBtn.dataset.handle || "";
      const title = decodeURIComponent(boxBtn.dataset.title || "") || boxBtn.textContent.trim();

      modal.dataset.selectedHandle = handle;
      modal.dataset.selectedBoxTitle = title;

      const grid = qs(".oeno-box-grid", modal);
      if (grid) qsa("[data-oeno-box]", grid).forEach((b) => b.classList.toggle("is-selected", b === boxBtn));

      // Reset duration state
      modal.dataset.selectedDuration = "";
      modal.dataset.selectedVariantId = "";
      modal.dataset.selectedSellingPlanId = "";
      modal.dataset.selectedDurationPrice = "";

      renderDurations(modal);
      return;
    }

    // Duration select
    const durBtn = e.target.closest("[data-oeno-duration]");
    if (durBtn) {
      e.preventDefault();

      modal.dataset.selectedDuration = durBtn.dataset.duration || "";
      modal.dataset.selectedVariantId = durBtn.dataset.variantId || "";
      modal.dataset.selectedSellingPlanId = durBtn.dataset.sellingPlanId || "";
      modal.dataset.selectedDurationPrice = decodeURIComponent(durBtn.dataset.price || "");

      const row = qs(".oeno-duration-row", modal);
      if (row) qsa("[data-oeno-duration]", row).forEach((c) => c.classList.toggle("is-selected", c === durBtn));

      return;
    }
  });

  // ESC close
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const modal = qs(`#${MODAL_ID}`);
    if (modal?.classList.contains("is-open")) closeModal(modal);
  });
})();